import React, { useEffect, useRef, useState } from 'react';
import {
  BookOpen,
  Activity,
  Stethoscope,
  UserRound,
  Bot,
  Shield,
  Settings,
  Wrench,
  Mail,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

const sectionIcons = {
  overview: <Activity size={18} />,
  'doctor-simulator': <Stethoscope size={18} />,
  'patient-simulator': <UserRound size={18} />,
  'medical-copilot': <Bot size={18} />,
  'privacy-safety': <Shield size={18} />,
  technical: <Settings size={18} />,
  troubleshooting: <Wrench size={18} />,
  contact: <Mail size={18} />,
};

const Help = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const headerRefs = useRef({});

  const toggleSection = (section) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  useEffect(() => {
    if (!expandedSection) return;

    const el = headerRefs.current[expandedSection];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.focus({ preventScroll: true });
    }
  }, [expandedSection]);

  const HelpSection = ({ id, title, children }) => (
    <div className="help-section">
      <button
        ref={(node) => {
          if (node) headerRefs.current[id] = node;
        }}
        className={`help-section-header ${expandedSection === id ? 'expanded' : ''}`}
        onClick={() => toggleSection(id)}
        type="button"
      >
        <div className="help-section-title-row">
          <span className="help-section-icon">{sectionIcons[id]}</span>
          <h3>{title}</h3>
        </div>
        <span className="expand-icon">
          {expandedSection === id ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </span>
      </button>
      {expandedSection === id && (
        <div className="help-section-content">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="help-page">
      <div className="help-container">
        <div className="help-header">
          <BookOpen size={32} style={{ color: 'var(--color-primary)', marginBottom: 12 }} />
          <h1>Help & Documentation</h1>
          <p className="help-subtitle">
            Complete guide to using Apollo Medical AI's training modules and features
          </p>
        </div>

        <div className="help-sections">
          <HelpSection id="overview" title="System Overview">
            <div className="help-content">
              <p><strong>Apollo Medical AI</strong> is a comprehensive medical training platform with three specialized modules:</p>
              <ul>
                <li><strong>Doctor Simulator:</strong> Practice medical interviews with AI patients</li>
                <li><strong>Patient Simulator:</strong> Experience consultations from the patient's perspective</li>
                <li><strong>Medical Copilot:</strong> Get AI-powered clinical decision support</li>
              </ul>
              <p>Each module uses advanced AI to provide realistic, educational interactions for medical professionals and students.</p>
            </div>
          </HelpSection>

          <HelpSection id="doctor-simulator" title="Doctor Simulator">
            <div className="help-content">
              <h4>Purpose</h4>
              <p>Practice your medical interview skills with realistic AI patients. Develop diagnostic reasoning and improve bedside manner.</p>

              <h4>How to Use</h4>
              <ol>
                <li><strong>Configure Condition:</strong> Choose the category of medical conditions that you would like to practice diagnosing.</li>
                <li><strong>Start Interview:</strong> Begin with open-ended questions like "What brings you in today?"</li>
                <li><strong>Gather Information:</strong> Ask about symptoms, medical history, family history, and social factors</li>
                <li><strong>Practice Skills:</strong> Work on active listening, empathy, and clinical reasoning</li>
                <li><strong>Complete the simulation:</strong> Come up with a diagnosis and treatment plan for the patient, for the AI to assess your overall performance.</li>
              </ol>

              <h4>Best Practices</h4>
              <ul>
                <li>Start with broad, open-ended questions</li>
                <li>Use follow-up questions to clarify details</li>
                <li>Practice explaining medical concepts in patient-friendly terms</li>
                <li>Work through your diagnostic reasoning systematically</li>
              </ul>
            </div>
          </HelpSection>

          <HelpSection id="patient-simulator" title="Patient Simulator">
            <div className="help-content">
              <h4>Purpose</h4>
              <p>Experience medical consultations from the patient's perspective. Learn effective communication and understand the patient experience.</p>

              <h4>How to Use</h4>
              <ol>
                <li><strong>Set Your Case:</strong> Input your chief complaint.</li>
                <li><strong>Describe Symptoms:</strong> Practice explaining your symptoms clearly</li>
                <li><strong>Ask Questions:</strong> Learn what questions patients should ask</li>
                <li><strong>Express Concerns:</strong> Simulate the ambiguity and uncertainty that patients face during doctor consults.</li>
              </ol>

              <h4>Learning Objectives</h4>
              <ul>
                <li>Understand patient perspectives and concerns</li>
                <li>Learn effective symptom description techniques</li>
                <li>Develop empathy for patient experiences</li>
                <li>Understand how Doctors tackle ambiguous symptoms and concerns</li>
              </ul>
            </div>
          </HelpSection>

          <HelpSection id="medical-copilot" title="Medical Copilot">
            <div className="help-content">
              <h4>Purpose</h4>
              <p>Get AI-powered clinical decision support for complex medical cases, research questions, and diagnostic challenges.</p>

              <h4>How to Use</h4>
              <ol>
                <li><strong>Configure Analysis:</strong> Initialize the case by giving chief complaint and other case details.</li>
                <li><strong>Update Case:</strong> Update the case whenever new info is available</li>
                <li><strong>Specify Request:</strong> Ask for specific analysis (diagnosis, treatment, prognosis)</li>
                <li><strong>Review Results:</strong> Analyze recommendations and supporting evidence</li>
              </ol>
            </div>
          </HelpSection>

          <HelpSection id="privacy-safety" title="Privacy & Safety">
            <div className="help-content">
              <h4>Data Privacy</h4>
              <p>Apollo Medical AI prioritizes user privacy and data security:</p>
              <ul>
                <li>All conversations are processed securely</li>
                <li>No personal medical information is stored permanently</li>
                <li>Data is used only for session-based AI responses</li>
                <li>Users can withdraw consent and clear data at any time</li>
              </ul>

              <h4>Safety Guidelines</h4>
              <p><strong>Important:</strong> This system is for educational and training purposes only.</p>
              <ul>
                <li><strong>Not for Real Patient Care:</strong> Never use for actual medical decisions</li>
                <li><strong>Simulation Only:</strong> All patient interactions are simulated</li>
                <li><strong>Educational Purpose:</strong> Designed for learning and skill development</li>
                <li><strong>Professional Supervision:</strong> Use under appropriate medical supervision</li>
              </ul>

              <h4>Consent Requirements</h4>
              <p>Users must provide informed consent to access AI modules, acknowledging:</p>
              <ul>
                <li>Educational and research use only</li>
                <li>No real medical advice provided</li>
                <li>Data collection and processing policies</li>
                <li>Ability to withdraw consent anytime</li>
              </ul>
            </div>
          </HelpSection>

          <HelpSection id="technical" title="Technical Information">
            <div className="help-content">
              <h4>System Requirements</h4>
              <ul>
                <li>Modern web browser (Chrome, Firefox, Safari, Edge)</li>
                <li>Stable internet connection</li>
                <li>JavaScript enabled</li>
              </ul>

              <h4>Technology Stack</h4>
              <ul>
                <li><strong>Frontend:</strong> React.js with responsive design</li>
                <li><strong>Backend:</strong> FastAPI with Python</li>
                <li><strong>AI Engine:</strong> OpenAI GPT models with Apollo Core</li>
                <li><strong>Security:</strong> HTTPS encryption and secure data handling</li>
              </ul>

              <h4>Performance Tips</h4>
              <ul>
                <li>Clear browser cache if experiencing issues</li>
                <li>Use latest browser version for best performance</li>
                <li>Check internet connection for slow responses</li>
                <li>Close unnecessary browser tabs for optimal performance</li>
              </ul>
            </div>
          </HelpSection>

          <HelpSection id="troubleshooting" title="Troubleshooting">
            <div className="help-content">
              <h4>Common Issues</h4>

              <div className="troubleshoot-item">
                <h5>AI Not Responding</h5>
                <ul>
                  <li>Check internet connection</li>
                  <li>Refresh the page</li>
                  <li>Clear browser cache</li>
                  <li>Try a different browser</li>
                </ul>
              </div>

              <div className="troubleshoot-item">
                <h5>Settings Not Saving</h5>
                <ul>
                  <li>Ensure JavaScript is enabled</li>
                  <li>Check browser console for errors</li>
                  <li>Try clearing browser data</li>
                </ul>
              </div>

              <div className="troubleshoot-item">
                <h5>Module Access Denied</h5>
                <ul>
                  <li>Complete consent form on homepage</li>
                  <li>Check consent status in sidebar</li>
                  <li>Refresh page after giving consent</li>
                </ul>
              </div>

              <div className="troubleshoot-item">
                <h5>Slow Performance</h5>
                <ul>
                  <li>Close other browser tabs</li>
                  <li>Check system memory usage</li>
                  <li>Try using a faster internet connection</li>
                  <li>Update browser to latest version</li>
                </ul>
              </div>
            </div>
          </HelpSection>

          <HelpSection id="contact" title="Support & Contact">
            <div className="help-content">
              <h4>Getting Help</h4>
              <p>If you need additional assistance:</p>
              <ul>
                <li><strong>Technical Issues:</strong> Contact us at e0959157@u.nus.edu</li>
                <li><strong>Educational Support:</strong> Contact us at e0959157@u.nus.edu</li>
                <li><strong>Feature Requests:</strong> Submit feedback at e0959157@u.nus.edu</li>
              </ul>

              <h4>System Information</h4>
              <ul>
                <li><strong>Version:</strong> Apollo Medical AI</li>
                <li><strong>Platform:</strong> Web-based application</li>
                <li><strong>Last Updated:</strong> August 2026</li>
              </ul>
            </div>
          </HelpSection>
        </div>
      </div>
    </div>
  );
};

export default Help;
